/**
 * The small amount of DOM handling both pages share.
 *
 * There is no framework here on purpose. The two pages are a drawing that
 * already exists in HTML and CSS, and the job is to put real figures into it,
 * not to re-express it in something that then has to be kept in step with it.
 *
 * Everything that reaches the page from Firestore goes through `text` or
 * `esc`. A unit id, a resident's name and the reason an accountant typed for
 * turning a payment down are all written by people, and one of them containing
 * a tag would otherwise be markup.
 */

import { t } from "./strings";
import type { Key, PluralKey } from "./strings";

export function el<E extends Element = HTMLElement>(id: string): E {
  const found = document.getElementById(id);
  if (!found) {
    throw new Error(`the page has no #${id}`);
  }
  return found as unknown as E;
}

/** Escapes everything HTML treats as syntax, for values built into a string. */
export function esc(value: string | number | null | undefined): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );
}

/** Fills every `data-t` on the page. Called again when the language changes. */
export function translate(root: ParentNode = document): void {
  for (const node of root.querySelectorAll<HTMLElement>("[data-t]")) {
    const key = node.dataset.t as Key | undefined;
    if (key !== undefined) {
      node.textContent = t(key);
    }
  }
  for (const node of root.querySelectorAll<HTMLElement>("[data-t-placeholder]")) {
    const key = node.dataset.tPlaceholder as Key | undefined;
    if (key !== undefined) {
      node.setAttribute("placeholder", t(key));
    }
  }
  document.documentElement.lang = t("common.language") === "English" ? "am" : "en";
}

export function say(key: Key | PluralKey, values?: Record<string, string | number>): string {
  return t(key, values);
}

/** A line at the bottom of the screen that says what just happened. */
export function toast(message: string): void {
  const node = document.getElementById("toast");
  if (!node) {
    return;
  }
  node.textContent = message;
  node.classList.add("show");
  window.setTimeout(() => node.classList.remove("show"), 2400);
}

/**
 * Runs the cards in on a fresh view, the way the drawing does.
 *
 * A honoured `prefers-reduced-motion` is already in the stylesheet, so this
 * only sets the delays.
 */
export function stagger(root: HTMLElement): void {
  const cards = root.querySelectorAll<HTMLElement>(".card, .grid-2 > *, .grid-3 > *, .grid-4 > *");
  cards.forEach((card, index) => {
    card.classList.add("anim-up");
    card.style.animationDelay = `${String(Math.min(index, 8) * 0.04)}s`;
  });
}

/**
 * Holds a button while the work behind it is in flight.
 *
 * Called synchronously from the click handler, before the callable is sent, so
 * the press is acknowledged in the same frame rather than a network round trip
 * later. The width is pinned first: the spinner replaces the label, and a
 * button that shrank to fit a spinner would drag its neighbours across.
 *
 * Disabling is not decoration here. Verifying a payment writes a ledger entry,
 * and the ledger is append only, so a second click while the first is still
 * going is a second entry that no one can delete.
 */
export function hold(button: HTMLButtonElement): () => void {
  button.style.minWidth = `${String(button.offsetWidth)}px`;
  button.dataset.busy = "";
  button.disabled = true;
  return () => {
    delete button.dataset.busy;
    button.disabled = false;
    button.style.minWidth = "";
  };
}

/** The button an event came from, when it is one. */
export function pressed(event: Event): HTMLButtonElement | null {
  const target = event.currentTarget;
  return target instanceof HTMLButtonElement ? target : null;
}

/**
 * Hands a file to the person at the keyboard.
 *
 * Built and released here rather than fetched from anywhere: the statement is
 * made from what this browser has already read, so there is nothing for a
 * server to do and nothing to wait for. The object URL is revoked because
 * without it the file stays in memory for as long as the tab is open, and this
 * is a button an accountant presses once a month for years.
 */
export function save(name: string, text: string, type = "text/csv;charset=utf-8"): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

let receiptScale = 1;

function paintReceiptScale(): void {
  const image = document.getElementById("receiptImage");
  if (image) {
    image.style.transform = `scale(${String(receiptScale)})`;
  }
}

export function showReceipt(url: string): void {
  const viewer = document.getElementById("receiptViewer");
  const image = document.getElementById("receiptImage") as HTMLImageElement | null;
  if (!viewer || !image) return;
  receiptScale = 1;
  image.src = url;
  image.alt = t("units.receipt");
  paintReceiptScale();
  viewer.classList.remove("hidden");
  document.getElementById("receiptClose")?.focus();
}

export function wireReceiptViewer(): void {
  const viewer = document.getElementById("receiptViewer");
  if (!viewer) return;
  const close = () => {
    viewer.classList.add("hidden");
    const image = document.getElementById("receiptImage") as HTMLImageElement | null;
    if (image) image.removeAttribute("src");
  };
  document.getElementById("receiptClose")?.addEventListener("click", close);
  viewer.addEventListener("click", (event) => {
    if (event.target === viewer) close();
  });
  document.getElementById("receiptZoomIn")?.addEventListener("click", () => {
    receiptScale = Math.min(3, receiptScale + 0.25);
    paintReceiptScale();
  });
  document.getElementById("receiptZoomOut")?.addEventListener("click", () => {
    receiptScale = Math.max(0.5, receiptScale - 0.25);
    paintReceiptScale();
  });
  document.getElementById("receiptZoomReset")?.addEventListener("click", () => {
    receiptScale = 1;
    paintReceiptScale();
  });
  document.getElementById("receiptFullscreen")?.addEventListener("click", () => {
    void viewer.requestFullscreen?.();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !viewer.classList.contains("hidden")) close();
  });
}
